<script setup lang="ts">
import { useGetMe } from "@/auth/useMe";
import DeleteButton from "@/components/DeleteButton.vue";
import HandleQueryStatus from "@/components/HandleQueryStatus.vue";
import ManagementLayout from "@/layouts/ManagementLayout.vue";
import { userRoles } from "@/users/roles";
import { useDeleteSelf } from "@/users/usersCrud";
import { formatDatetime } from "@/utils/toFormattedDatetime";
import { Plus, Trash } from "@iconoir/vue";
import { ref } from "vue";
import {
  useDeletePasskey,
  useListPasskeys,
  useRegisterPasskey,
} from "./passkeysCrud";

const { me, getMeStatus } = useGetMe();
const { passkeys, listPasskeysStatus } = useListPasskeys();
const {
  registerPasskey,
  registerPasskeyStatus,
  isError: registerError,
} = useRegisterPasskey();
const { deletePasskey, deletePasskeyStatus } = useDeletePasskey();
const { deleteSelf, resetDeleteSelf, deleteSelfStatus } = useDeleteSelf();

const newPasskeyName = ref("");

function onRegister() {
  registerPasskey(newPasskeyName.value.trim() || undefined, {
    onSuccess: () => {
      newPasskeyName.value = "";
    },
  });
}
</script>

<template>
  <ManagementLayout>
    <template #header>
      <h1>Mein Konto</h1>
    </template>

    <HandleQueryStatus :query-status="getMeStatus">
      <section v-if="me" class="box column">
        <h2>Profil</h2>
        <dl class="profile-list">
          <div>
            <dt>Name</dt>
            <dd>{{ me.name }}</dd>
          </div>
          <div>
            <dt>E-Mail</dt>
            <dd>{{ me.email }}</dd>
          </div>
          <div>
            <dt>Rolle</dt>
            <dd>{{ userRoles[me.role] ?? me.role }}</dd>
          </div>
        </dl>
      </section>
    </HandleQueryStatus>

    <section class="box column">
      <h2>Passkeys</h2>

      <HandleQueryStatus :query-status="listPasskeysStatus">
        <ul v-if="passkeys && passkeys.length > 0" role="list" class="column">
          <li
            v-for="passkey in passkeys"
            :key="passkey.id"
            class="passkey-item row"
          >
            <div class="column-self-space-auto">
              <span>{{ passkey.name ?? "Unbenannt" }}</span>
              <span class="passkey-date">{{
                formatDatetime(passkey.createdAt)
              }}</span>
            </div>
            <DeleteButton
              class="button button--tertiary"
              :deleting-status="deletePasskeyStatus"
              @confirmed="() => deletePasskey(passkey.id)"
            >
              <Trash />
              <span class="visually-hidden"> Löschen </span>
              <template #confirmation>
                Möchtest du den Passkey "{{ passkey.name ?? "Unbenannt" }}"
                wirklich löschen?
              </template>
            </DeleteButton>
          </li>
        </ul>
        <p v-else-if="passkeys && passkeys.length === 0" class="muted">
          Noch keine Passkeys registriert.
        </p>
      </HandleQueryStatus>

      <h3>Neuen Passkey registrieren</h3>
      <form class="column" @submit.prevent="onRegister">
        <label for="passkey-name"
          >Name (optional, z.B. „Mein Smartphone")</label
        >
        <input
          id="passkey-name"
          v-model="newPasskeyName"
          type="text"
          placeholder="Passkey-Name"
          :disabled="registerPasskeyStatus === 'pending'"
        />
        <p v-if="registerError" class="error">
          Passkey konnte nicht registriert werden. Bitte erneut versuchen.
        </p>
        <button
          class="button"
          type="submit"
          :disabled="registerPasskeyStatus === 'pending'"
        >
          <Plus /> Passkey hinzufügen
        </button>
      </form>
    </section>
    <section class="box column">
      <h2>Konto löschen</h2>
      <form class="column" @submit.prevent="() => {}">
        <DeleteButton
          class="button button--delete-self"
          :deleting-status="deleteSelfStatus"
          @initiated="resetDeleteSelf"
          @confirmed="deleteSelf"
        >
          <Trash /> Konto löschen
          <template #confirmation>
            Möchtest du dein Konto wirklich löschen?
          </template>
        </DeleteButton>
      </form>
    </section>
  </ManagementLayout>
</template>

<style scoped>
.profile-list {
  display: grid;
  gap: var(--spacing-sm);

  > div {
    display: flex;
    gap: var(--spacing-md);
    align-items: baseline;
  }

  dt {
    color: var(--text-color-muted);
    min-inline-size: 6ch;
  }
}

.passkey-item {
  align-items: center;
  padding-block: var(--spacing-sm);
}

.passkey-date {
  display: block;
  font-size: var(--font-size-sm);
  color: var(--text-color-muted);
}

.error {
  color: var(--red-400);
}

:deep(.button--delete-self) {
  background-color: var(--red-500);

  &:hover {
    background-color: var(--red-400);
  }
}
</style>
